import { useCallback } from 'react';
import type { DataTableCol, DataTableColMeta } from '../column-builder';
import { useDynamicSearch } from '@/hooks/use-dynamic-search';
import type { DataTableRowModel } from '../types';

type SearchRequestParams = Record<string, any>;

interface UseTableSearchProps<TSearchParams extends object> {
  endpoint: string;
  pageSize: number;
  sumField?: string;
  searchData: (filters: SearchRequestParams, page?: number, searchPageSize?: number) => Promise<void>;
  searchParams: TSearchParams;
  setSearchParams: (params: TSearchParams) => void;
  isFiltered: boolean;
  handlePageChange: (page: number) => Promise<void>;
  getColumnMeta: () => DataTableColMeta[];
  mapFormDataToSearchParams?: (formData: SearchRequestParams) => TSearchParams;
}

const defaultMapFormDataToSearchParams = <TSearchParams extends object>(formData: SearchRequestParams) => {
  const { dynamic_filters, ...nextSearchParams } = formData;
  return nextSearchParams as TSearchParams;
};

export const buildColMeta = <TRow extends DataTableRowModel>(
  columns: ReadonlyArray<DataTableCol<TRow>>
): DataTableColMeta[] =>
  columns.map(column => ({
    field: column.field,
    type: column.columnType
  }));

export const useTableSearch = <TSearchParams extends object>({
  endpoint,
  pageSize,
  sumField,
  searchData,
  searchParams,
  setSearchParams,
  isFiltered,
  handlePageChange,
  getColumnMeta,
  mapFormDataToSearchParams = defaultMapFormDataToSearchParams<TSearchParams>
}: UseTableSearchProps<TSearchParams>) => {
  const onSearch = useCallback(
    async (params: SearchRequestParams) => {
      await searchData(params);
    },
    [searchData]
  );

  const onInitialSearch = useCallback(
    async (formData: SearchRequestParams, onSearchFn: (params: SearchRequestParams) => Promise<void>) => {
      setSearchParams(mapFormDataToSearchParams(formData));
      await onSearchFn(formData);
    },
    [mapFormDataToSearchParams, setSearchParams]
  );

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch,
    params: searchParams,
    onInitialSearch,
    getColumns: getColumnMeta,
    endpoint,
    pageSize,
    sumField
  });

  const onPageChange = useCallback(
    async (page: number) => {
      if (hasFilterParams) {
        await handleSearchPageChange(page);
        return;
      }

      if (isFiltered) {
        await searchData(searchParams as SearchRequestParams, page);
        return;
      }

      await handlePageChange(page);
    },
    [handlePageChange, handleSearchPageChange, hasFilterParams, isFiltered, searchData, searchParams]
  );

  return {
    searchState,
    handleSearchChange,
    onPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering: isSearching
  };
};
